import { useEffect, useState } from "react";
import { api, formatTL } from "@/lib/api";
import { toast } from "sonner";
import { Package, ShoppingBag, TrendingUp, Clock, Bike, Store, Link as LinkIcon } from "lucide-react";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso || "-";
  }
};

const computeParams = (period, range) => {
  const now = new Date();
  const p = { tz_offset: -now.getTimezoneOffset() };
  if (period === "today") {
    p.start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    p.end = now.toISOString();
  } else if (period === "month") {
    p.start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    p.end = now.toISOString();
  } else {
    if (range.start) p.start = new Date(range.start + "T00:00:00").toISOString();
    if (range.end) p.end = new Date(range.end + "T23:59:59").toISOString();
  }
  return p;
};

export const ReportsPanel = () => {
  const [period, setPeriod] = useState("today");
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/reports", { params: computeParams(period, range) })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Rapor yüklenemedi"));
  }, [period, range]);

  const maxHour = data ? Math.max(1, ...data.hourly.map((h) => h.quantity)) : 1;
  const maxProd = data && data.products.length ? Math.max(1, ...data.products.map((p) => p.quantity)) : 1;

  const summary = [
    { label: "Ciro", value: data ? formatTL(data.total_revenue) : "-", icon: TrendingUp, color: "text-[var(--fb-red)]" },
    { label: "Sipariş", value: data ? data.total_orders : "-", icon: ShoppingBag, color: "text-[var(--fb-amber)]" },
    { label: "Satılan Ürün Adedi", value: data ? data.total_items : "-", icon: Package, color: "text-green-600" },
  ];

  return (
    <div>
      {/* Dönem seçici */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex gap-1 bg-white rounded-full p-1 border border-[var(--fb-border)]">
          {[
            ["today", "Günlük"],
            ["month", "Aylık"],
            ["range", "Tarih Aralığı"],
          ].map(([v, l]) => (
            <button
              key={v}
              data-testid={`report-period-${v}`}
              onClick={() => setPeriod(v)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                period === v ? "bg-[var(--fb-red)] text-white" : "text-[var(--fb-muted)]"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        {period === "range" && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              data-testid="report-start"
              value={range.start}
              onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
              className="bg-white border border-[var(--fb-border)] rounded-full px-3 py-1.5 text-sm outline-none focus:border-[var(--fb-red)]"
            />
            <span className="text-[var(--fb-muted)]">—</span>
            <input
              type="date"
              data-testid="report-end"
              value={range.end}
              onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
              className="bg-white border border-[var(--fb-border)] rounded-full px-3 py-1.5 text-sm outline-none focus:border-[var(--fb-red)]"
            />
          </div>
        )}
      </div>

      {/* Özet kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {summary.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-2xl border border-[var(--fb-border)] p-5 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">{c.label}</p>
                <p className="font-display font-extrabold text-2xl mt-1">{c.value}</p>
              </div>
              <Icon className={`w-9 h-9 ${c.color}`} />
            </div>
          );
        })}
      </div>

      {/* Sipariş tipine göre kırılım (Paket Servis ayrı) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {(() => {
          const types = data?.by_type || [];
          const findT = (name) => types.find((t) => t.order_type === name) || { orders: 0, revenue: 0, items: 0 };
          const delivery = findT("Paket Servis");
          const takeaway = findT("Gel-Al");
          const cards = [
            { key: "Paket Servis", label: "Paket Servis", icon: Bike, accent: "border-[var(--fb-red)] bg-red-50", iconColor: "text-[var(--fb-red)]", t: delivery },
            { key: "Gel-Al", label: "Gel-Al / Kiosk", icon: Store, accent: "border-[var(--fb-border)] bg-white", iconColor: "text-[var(--fb-amber)]", t: takeaway },
            { key: "Link", label: "Online Link", icon: LinkIcon, accent: "border-blue-200 bg-blue-50", iconColor: "text-blue-600", t: findT("Link") },
          ];
          return cards.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.key}
                data-testid={`report-type-${c.key}`}
                className={`rounded-2xl border p-5 ${c.accent}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="font-display font-bold flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${c.iconColor}`} /> {c.label}
                  </p>
                  <span className="text-xs font-semibold text-[var(--fb-muted)]">
                    {c.t.orders} sipariş
                  </span>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">Ciro</p>
                    <p data-testid={`report-type-revenue-${c.key}`} className="font-display font-extrabold text-2xl mt-0.5">
                      {data ? formatTL(c.t.revenue) : "-"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">Ürün</p>
                    <p className="font-bold text-lg mt-0.5">{c.t.items} adet</p>
                  </div>
                </div>
              </div>
            );
          });
        })()}
      </div>

      {/* Platform kırılımı (yemek platformları) */}
      {data?.by_platform?.length > 0 &&
        (() => {
          const maxPlatRev = Math.max(1, ...data.by_platform.map((x) => x.revenue));
          return (
            <div
              data-testid="report-platform-breakdown"
              className="bg-white rounded-2xl border border-[var(--fb-border)] p-5 mb-6"
            >
              <h3 className="font-display font-bold mb-4 flex items-center gap-2">
                <Bike className="w-5 h-5 text-[var(--fb-red)]" /> Platform Bazlı Paket Servis
              </h3>
              <div className="space-y-3">
                {data.by_platform.map((p) => (
                  <div key={p.platform} data-testid={`report-platform-${p.platform}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-semibold truncate mr-2">{p.platform}</span>
                      <span className="text-[var(--fb-muted)] whitespace-nowrap">
                        <span className="text-[var(--fb-ink)] font-bold">{p.orders} sipariş</span> · {p.items} adet ·{" "}
                        <span className="text-[var(--fb-red)] font-bold">{formatTL(p.revenue)}</span>
                      </span>
                    </div>
                    <div className="h-2.5 bg-[var(--fb-neutral)] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[var(--fb-red)] rounded-full transition-all"
                        style={{ width: `${(p.revenue / maxPlatRev) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

      {/* Link siparişleri */}
      <div data-testid="report-link-orders" className="bg-white rounded-2xl border border-[var(--fb-border)] p-5 mb-6">
        <h3 className="font-display font-bold mb-4 flex items-center gap-2">
          <LinkIcon className="w-5 h-5 text-blue-600" /> Link Siparişleri
        </h3>
        {!data || (data.link_orders?.length || 0) === 0 ? (
          <p data-testid="report-link-empty" className="text-sm text-[var(--fb-muted)] text-center py-8">
            Bu dönemde link siparişi yok.
          </p>
        ) : (
          <div className="overflow-x-auto fb-scroll">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[var(--fb-muted)] border-b border-[var(--fb-border)]">
                  <th className="py-2 pr-3 font-semibold">No</th>
                  <th className="py-2 pr-3 font-semibold">Tarih</th>
                  <th className="py-2 pr-3 font-semibold">Müşteri</th>
                  <th className="py-2 pr-3 font-semibold">Telefon</th>
                  <th className="py-2 pr-3 font-semibold">Ödeme</th>
                  <th className="py-2 pr-3 font-semibold">Durum</th>
                  <th className="py-2 pl-3 font-semibold text-right">Tutar</th>
                </tr>
              </thead>
              <tbody>
                {data.link_orders.map((o) => (
                  <tr key={o.order_number} data-testid={`report-link-order-${o.order_number}`} className="border-b border-[var(--fb-border)] last:border-0">
                    <td className="py-2 pr-3 font-bold">#{o.order_number}</td>
                    <td className="py-2 pr-3 whitespace-nowrap text-[var(--fb-muted)]">{fmtDate(o.created_at)}</td>
                    <td className="py-2 pr-3">{o.customer_name || "-"}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{o.customer_phone || "-"}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{o.payment_method || "-"}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{o.status || "-"}</td>
                    <td className="py-2 pl-3 text-right font-bold text-[var(--fb-red)] whitespace-nowrap">{formatTL(o.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ürün bazlı satış */}
        <div className="bg-white rounded-2xl border border-[var(--fb-border)] p-5">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-[var(--fb-red)]" /> Ürün Satışları
          </h3>
          {!data || data.products.length === 0 ? (
            <p data-testid="report-products-empty" className="text-sm text-[var(--fb-muted)] text-center py-8">
              Bu dönemde satış yok.
            </p>
          ) : (
            <div className="space-y-3">
              {data.products.map((p) => (
                <div key={p.name} data-testid={`report-product-${p.name}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-semibold truncate mr-2">{p.name}</span>
                    <span className="text-[var(--fb-muted)] whitespace-nowrap">
                      <span className="text-[var(--fb-ink)] font-bold">{p.quantity} adet</span> · {formatTL(p.revenue)}
                    </span>
                  </div>
                  <div className="h-2.5 bg-[var(--fb-neutral)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[var(--fb-red)] rounded-full transition-all"
                      style={{ width: `${(p.quantity / maxProd) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Saatlik satış */}
        <div className="bg-white rounded-2xl border border-[var(--fb-border)] p-5">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[var(--fb-red)]" /> Saatlik Satış (adet)
          </h3>
          {!data ? (
            <p className="text-sm text-[var(--fb-muted)] text-center py-8">Yükleniyor...</p>
          ) : (
            <div className="flex items-end gap-1 h-44 overflow-x-auto fb-scroll pb-1">
              {data.hourly.map((h) => (
                <div
                  key={h.hour}
                  data-testid={`report-hour-${h.hour}`}
                  className="flex flex-col items-center gap-1 shrink-0"
                  title={`${String(h.hour).padStart(2, "0")}:00 — ${h.quantity} adet · ${formatTL(h.revenue)}`}
                >
                  <div className="flex-1 flex items-end w-6">
                    <div
                      className={`w-full rounded-t ${h.quantity ? "bg-[var(--fb-red)]" : "bg-[var(--fb-neutral)]"}`}
                      style={{ height: `${Math.max(4, (h.quantity / maxHour) * 130)}px` }}
                    />
                  </div>
                  <span className="text-[10px] text-[var(--fb-muted)]">{String(h.hour).padStart(2, "0")}</span>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-[var(--fb-muted)] mt-2">Saatler yerel saat dilimine göredir.</p>
        </div>
      </div>
    </div>
  );
};
